"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  MdArrowForward,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
} from "react-icons/md";

import styles from "./recommended-locations.module.css";
import { makeLocalizedPublicHref } from "@/lib/localizedRoutes/routeHelpers";

export type RecommendedLocation = {
  name: string;
  slug: string[];
  image: string;
  properties: string;
  from: string;
  trend: string;
};

type RecommendedLocationsProps = {
  isGlobal: boolean;
  marketName: string;
  featured: RecommendedLocation[];
  domain: string;
  locale: string;
  locationsHref: string;
};

export default function RecommendedLocations({
  isGlobal,
  marketName,
  featured,
  domain,
  locale,
  locationsHref,
}: RecommendedLocationsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const handleScrollLeft = () => {
    if (!scrollRef.current) return;

    const { clientWidth } = scrollRef.current;
    scrollRef.current.scrollBy({ left: -clientWidth, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    if (!scrollRef.current) return;

    const { clientWidth } = scrollRef.current;
    scrollRef.current.scrollBy({ left: clientWidth, behavior: "smooth" });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;

    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;

    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;

    scrollRef.current.scrollLeft = scrollLeft - walk;

    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.titleBlock}>
          <h2>
            {isGlobal
              ? "Featured countries worldwide"
              : `Locații recomandate în ${marketName}`}
          </h2>
          <span className={styles.subtitle}>{marketName}</span>
        </div>

        <Link href={locationsHref} className={styles.viewAllLink}>
          {isGlobal ? "View all" : "Vezi toate"}
          <MdArrowForward aria-hidden="true" />
        </Link>
      </div>

      <div className={styles.sliderWrapper}>
        <div
          ref={scrollRef}
          className={`${styles.cardsTrack} ${
            isDragging ? styles.dragging : ""
          }`}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          {featured.map((item) => (
            <Link
              key={item.name}
              href={makeLocalizedPublicHref(
                domain,
                locale,
                "locations",
                item.slug,
              )}
              className={styles.featuredCard}
              onClick={(e) => {
                if (hasDragged) e.preventDefault();
              }}
            >
              <div className={styles.imageWrapper}>
                <Image
                  src={item.image}
                  alt={item.name}
                  fill
                  sizes="(max-width: 768px) 280px, 320px"
                  className={styles.cardImage}
                  draggable={false}
                />
                <div className={styles.cardShade} />
              </div>

              <div className={styles.featuredContent}>
                <div className={styles.contentHeader}>
                  <h3>{item.name}</h3>
                  <span className={styles.trendBadge}>{item.trend}</span>
                </div>

                <div className={styles.cardMetrics}>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>
                      {isGlobal ? "Properties" : "Proprietăți"}
                    </span>
                    <span className={styles.metricValue}>
                      {item.properties}
                    </span>
                  </div>

                  <div className={styles.metricDivider} />

                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>
                      {isGlobal ? "Starting at" : "Preț de la"}
                    </span>
                    <span className={styles.metricValue}>{item.from}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className={styles.carouselControls}>
          <button
            className={styles.controlBtn}
            aria-label="Previous"
            onClick={handleScrollLeft}
          >
            <MdOutlineChevronLeft />
          </button>
          <button
            className={styles.controlBtn}
            aria-label="Next"
            onClick={handleScrollRight}
          >
            <MdOutlineChevronRight />
          </button>
        </div>
      </div>
    </section>
  );
}
